import cn from 'classnames';
import * as d3 from 'd3';
import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCurrentQuota } from '../../contexts/CurrentQuota';

import { API } from '../../API';
import Modal from '../../components/Modal';
import { Paginator } from '../../components/Paginator/Paginator';
import { Loader } from '../../components/Loader/Loader';
import { Search } from '../../components/Search/Search';
import { ReactComponent as PlusIcon } from '../../assets/plus.svg';
import { ReactComponent as ArrowIcon } from '../NarrativePage/assets/arrow.svg';
import { ReactComponent as ArrowDownIcon} from '../NarrativePage/assets/arrowDown.svg';
import { ReactComponent as SortIcon} from '../../assets/sort.svg';
import { ReactComponent as FolderIcon} from './assets/projectFolder.svg';
import { ReactComponent as EditIcon} from '../../assets/edit-new.svg';
import { ReactComponent as DeleteIcon} from '../../assets/delete-new.svg';
import { DropdownMenu } from '../../components/DropdownMenu/DropdownMenu';
import { Radiobutton } from '../../components/DropdownMenu/Radiobutton';
import { SortingOrder } from '../../components/DropdownMenu/SortingOrder';
import { Tooltip } from 'react-tooltip';
import { formatNumber } from '../../utils/fomatNumbers';
import { toUTCDateString } from '../../utils/utcDateString';
import { format } from 'date-fns';
import { ProjectTabs } from '../../components/ProjectTabs/ProjectTabs';
import CreateProjectModal from '../../components/CreateProjectModal/CreateProjectModal';
import ManageProjectsModal from '../../components/ManageProjectsModal/ManageProjectsModal';
import MoveToProjectModal from '../../components/MoveToProjectModal/MoveToProjectModal';
import { projectsApi } from '../../utils/projectsApi';
import styles from './NarrativesPage.module.scss';

const sortingFieldNames = {
  ID: 'id',
  NAME: 'name',
  AUTHOR: ['last_name', 'first_name'],
  IS_ACTIVE: 'is_active',
  TOTAL_MESSAGES: 'total_messages',
  NEW_MESSAGES: 'new_messages',
};

const mapStatusClasses = {
  'active': styles.active,
  'inactive': styles.inactive
}

export function NarrativesPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [currentQuota, setCurrentQuota] = useCurrentQuota();
  const [narratives, setNarratives] = useState(null);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({
    isActive: false,
    id: null,
  });
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const page = searchParams.get('page') || 1;
  const searchQuery = searchParams.get('q');
  const _rawProject = searchParams.get('project');
  const _parsedProject = _rawProject !== null ? parseInt(_rawProject, 10) : null;
  const activeProjectId = _parsedProject !== null && Number.isSafeInteger(_parsedProject) ? _parsedProject : null;
  const [dropdownMenu, setDropdownMenu] = useState(false);
  const [sorting, setSorting] = useState({isAscending: false, fieldName: 'id'});

  const [projects, setProjects] = useState([]);
  const [projectsLoaded, setProjectsLoaded] = useState(false);
  const [projectsError, setProjectsError] = useState(false);
  const [createProjectModalOpen, setCreateProjectModalOpen] = useState(false);
  const [manageProjectsModalOpen, setManageProjectsModalOpen] = useState(false);
  const manageModalOpenCaseIdsRef = useRef(null);
  const [moveToProjectModal, setMoveToProjectModal] = useState({ isOpen: false, caseId: null, sourceProjectId: null });

  const sortingQuery = sorting
    ? `${sorting.isAscending ? '' : '-'}${sorting.fieldName}`
    : null;

  useEffect(() => {
    if (location.state?.reload && location.state?.reload === true) {
      setSorting({isAscending: false, fieldName: 'id'});
      setNarratives(null);
    }
  }, [location.state]);

  useEffect(() => {
    const raw = searchParams.get('project');
    if (raw !== null && !Number.isSafeInteger(parseInt(raw, 10))) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('project');
        return next;
      });
    }
  }, [searchParams, setSearchParams]);

  const fetchProjects = useCallback(() => {
    projectsApi.list().then((data) => {
      setProjects(Array.isArray(data) ? data : []);
      setProjectsError(false);
      setProjectsLoaded(true);
    }).catch((err) => {
      console.error('Failed to load projects:', err);
      setProjectsError(true);
      setProjectsLoaded(true);
    });
  }, []);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  const fetchNarratives = useCallback(() => {
    setNarratives(null);
    const urlParams = new URLSearchParams();
    urlParams.set('size', '50');
    urlParams.set('page', page);
    urlParams.set('is_threat', false);

    if (searchQuery) {
      urlParams.set('q', searchQuery);
    }

    if (activeProjectId) {
      urlParams.set('project_id', activeProjectId);
    }

    if (sorting) {
      if (Array.isArray(sorting.fieldName)) {
        sorting.fieldName.forEach((element) => {
          urlParams.append(
            'sorting',
            `${sorting.isAscending ? '' : '-'}${element}`,
          );
        });
      } else {
        urlParams.set('sorting', sortingQuery);
      }
    }

    API.fetch('GET', `/API/v1/narratives?${urlParams.toString()}`).then(
      (data) => {
        setNarratives(data);
      },
    );
  }, [page, searchQuery, sortingQuery, sorting, activeProjectId]);

  const handleTabChange = useCallback((id) => {
    const params = { page: 1 };
    if (id !== null) params.project = id;
    setSearchParams(params);
  }, [setSearchParams]);

  useEffect(() => {
    if (projectsLoaded && activeProjectId && !projects.find((p) => p.id === activeProjectId)) {
      handleTabChange(null);
    }
  }, [projects, projectsLoaded, activeProjectId, handleTabChange]);

  const handleOpenManageProjects = useCallback(() => {
    const activeProject = projects.find((p) => p.id === activeProjectId);
    manageModalOpenCaseIdsRef.current = [...(activeProject?.case_ids ?? [])].sort().join(',');
    setManageProjectsModalOpen(true);
  }, [projects, activeProjectId]);

  const handleCloseManageProjects = useCallback(() => {
    setManageProjectsModalOpen(false);
    if (activeProjectId) {
      const activeProject = projects.find((p) => p.id === activeProjectId);
      const newCaseIds = [...(activeProject?.case_ids ?? [])].sort().join(',');
      if (newCaseIds !== manageModalOpenCaseIdsRef.current) {
        fetchNarratives();
      }
    }
  }, [projects, activeProjectId, fetchNarratives]);

  const handleProjectCreated = useCallback((newProject) => {
    setProjects((prev) => [...prev, newProject]);
    handleTabChange(newProject.id);
  }, [handleTabChange]);

  const handleMoveSuccess = useCallback(({ caseId, targetProjectId }) => {
    setProjects((prev) =>
      prev.map((p) => {
        if (p.id === moveToProjectModal.sourceProjectId) {
          return { ...p, case_ids: (p.case_ids ?? []).filter((id) => id !== caseId) };
        }
        if (p.id === targetProjectId) {
          return { ...p, case_ids: [...(p.case_ids ?? []), caseId] };
        }
        return p;
      }),
    );
    // the list is server-filtered by project, so the moved case must drop out of this tab
    fetchNarratives();
  }, [moveToProjectModal.sourceProjectId, fetchNarratives]);

  const handleDelete = useCallback(
    (id) => {
      setIsDeleting(true);
      API.fetch('DELETE', `/API/v1/narratives/${id}`)
        .then(() => {
          setProjects((prev) =>
            prev.map((p) => ({ ...p, case_ids: (p.case_ids ?? []).filter((cid) => cid !== id) })),
          );
          setDeleteConfirmModal({ isActive: false, id: null });
          fetchNarratives();
        })
        .catch(() => {
          setDeleteConfirmModal({ isActive: false, id: null });
        })
        .finally(() => {
          setIsDeleting(false);
        });
    },
    [fetchNarratives],
  );

  useEffect(fetchNarratives, [fetchNarratives]);

  const displayedNarratives = narratives;

  const handleSortingClick = (fieldName) => {
    const isAscending =
      sorting && sorting.fieldName === fieldName ? !sorting.isAscending : true;

    setSorting({ isAscending, fieldName });
  };

  const sortingMenu = [
    {
      name: t('Identifier'),
      value: 'id',
    },
    {
      name: t('Name'),
      value: 'name',
    },
    {
      name: t('Author'),
      value: 'last_name',
    },
    {
      name: t('Status'),
      value: 'is_active',
    },
    {
      name: t('Total publications'),
      value: 'total_messages',
    },
    {
      name: t('Unread'),
      value: 'new_messages',
    },
    {
      name: t('Start date'),
      value: 'start_date',
    },
    {
      name: t('End date'),
      value: 'end_date',
    },
  ]

  const sortingOrder = [
    {
      name: t('A-Z'),
      icon: <ArrowIcon/>,
      value: true,
    },
    {
      name: t('Z-A'),
      value: false,
      icon: <ArrowDownIcon/>,
    },
  ]

  return (
    <div className="list-content">
      <div className="page-header">
        <div className="breadcrumb">
          <div className="breadcrumb">
            <span>{t('Cases')}</span>
          </div>
        </div>
        <div className="controls">
          <button
            className='button new-button'
            onClick={() => {
            navigate('/narratives/new')
            }}
            disabled={currentQuota?.billing_mode && currentQuota?.quota?.ANALYTICAL?.balance <= 0}
            data-tooltip-id="create-case"
            data-tooltip-content={currentQuota?.billing_mode && currentQuota?.quota?.ANALYTICAL?.balance > 0 ? t("Create case") : t("No quota left")}
          >
            <PlusIcon />
            {t('Create case')}
          </button>
          <Tooltip id="create-case" place="bottom"/>
        </div>
      </div>

      {projectsLoaded && !projectsError && (
        <ProjectTabs
          projects={projects}
          activeTabId={activeProjectId}
          onTabChange={handleTabChange}
          onCreateProject={() => setCreateProjectModalOpen(true)}
          onManageProjects={handleOpenManageProjects}
        />
      )}
      {projectsLoaded && projectsError && (
        <p role="alert" style={{ color: 'var(--clr-danger, #dc2626)', fontSize: '0.875rem', margin: '8px 0' }}>
          {t('Failed to load projects.')}{' '}
          <button
            type="button"
            onClick={fetchProjects}
            style={{ background: 'none', border: 'none', color: 'inherit', textDecoration: 'underline', cursor: 'pointer', padding: 0 }}
          >
            {t('Retry')}
          </button>
        </p>
      )}

      <div className="next-card-header">
        <div className={styles.searchWrapper}>
          <Search
            key={activeProjectId ?? 'all'}
            className={styles.searchInput}
            onChange={value => {
              setSearchParams({ ...(activeProjectId && { project: activeProjectId }), q: value, page: 1 })
            }}
          />

          <DropdownMenu  
              isOpen={dropdownMenu}
              header={t('Sort by')}
              onClick={() => setDropdownMenu(!dropdownMenu)}
              buttonName={t('Sort')}
              icon={<SortIcon/>}
            >
              <Radiobutton 
                itemsList={sortingMenu}
                current={sorting.fieldName}
                onChange={(value) => {
                  setSorting({...sorting, fieldName: value})
                  if(searchQuery) {
                    setSearchParams({ ...(activeProjectId && { project: activeProjectId }), q: searchQuery, page: 1 })
                  } else {
                    setSearchParams({ ...(activeProjectId && { project: activeProjectId }), page: 1 })
                  }
                }}
              />

              <SortingOrder 
                itemsList={sortingOrder}
                onClick={value => {
                  setSorting({...sorting, isAscending: value})
                  if(searchQuery) {
                    setSearchParams({ ...(activeProjectId && { project: activeProjectId }), q: searchQuery, page: 1 })
                  } else {
                    setSearchParams({ ...(activeProjectId && { project: activeProjectId }), page: 1 })
                  }
                }}
                current={sorting.isAscending}
              />
            </DropdownMenu>
        </div>
        
      </div>

      {!narratives ? <Loader /> : displayedNarratives?.objects?.length > 0 ? <div className={styles.tableWrapper}>
          <table className={styles.cases}>
            <thead>
              <tr>
                <td>{t('Case')}</td>
                <td>{t('Case type')}</td>
                <td>{t('Publications')}</td>
                <td>{t('Unread')}</td>
                <td>{t('Start date')}</td>
                <td>{t('End date')}</td>
                <td></td>
              </tr>
            </thead>
            <tbody>
              {displayedNarratives?.objects?.map((narrative) => {
                let isNarrativeActive = narrative.is_active === true ? 'active' : 'inactive'
                
                let caseType = null;
                const isManual = (narrative.type === "NARRATIVE" && narrative.is_manual === true) || narrative.type === "MANUAL";
                const publicationDate = (value) => (value ? format(new Date(toUTCDateString(value)), 'yyyy-MM-dd') : null);
                const startDate = (isManual && publicationDate(narrative.first_message_date_publicated)) || narrative.parameters?.start_date || '';
                const endDate = (isManual && publicationDate(narrative.last_message_date_publicated)) || narrative.parameters?.end_date || '';

                if(isManual) {
                  caseType = t('Manual')
                } else if (narrative.type === "NARRATIVE") {
                  caseType = t('Narrative')
                } else if (narrative.type === "INCIDENT") {
                  caseType = t('Incident')
                } else if (narrative.type === "IMPACT_ASSESSMENT") {
                  caseType = t('Impact assessment')
                } else if (narrative.type === "TOPIC_MODELING") {
                  caseType = t('Topic modeling')
                }

                return (
                  <tr key={narrative.id}>
                    <td>
                      <div className={styles.caseNameWrapper}>
                        <div className={styles.caseNameContainer}>
                          <span>
                            <Link to={`/narratives/${narrative.id}`}>
                              {narrative.name}
                            </Link>
                          </span>
                          <div className={styles.caseInfo}>
                            <span>#{narrative.id}</span>
                            <span>&#8226;</span>
                            <span>
                              {t('created by1 ')}<span className={styles.userName}>{narrative?.user?.first_name} {narrative?.user?.last_name}</span>
                            </span>
                            <span>&#8226;</span>
                            <div className={styles.statusWrapper}><span className={`${mapStatusClasses[isNarrativeActive]}`}>{narrative.is_active ? t('Active') : t('Inactive')}</span> </div>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>{caseType}</td>
                    <td>{formatNumber(narrative.total_messages)}</td>
                    <td>{formatNumber(narrative.new_messages)}</td>
                    <td>
                      <span className={styles.caseDate}>
                        {startDate}
                      </span>
                    </td>
                    <td>
                      <span className={styles.caseDate}>
                        {endDate}
                      </span>
                    </td>
                    <td>
                      <div className={styles.controls}>
                        <button
                          type="button"
                          className={styles.actionBtn}
                          data-tooltip-id="move-case"
                          data-tooltip-content={t('Move to project')}
                          onClick={() => {
                            const sourceProjectId = projects.find((p) => p.case_ids?.includes(narrative.id))?.id ?? null;
                            setMoveToProjectModal({ isOpen: true, caseId: narrative.id, sourceProjectId });
                          }}
                        >
                          <FolderIcon />
                        </button>

                        <Link
                          to={`/narratives/${narrative.id}/edit`}
                          className={styles.actionBtn}
                          data-tooltip-id="edit-case"
                          data-tooltip-content={t('Edit')}
                        >
                          <EditIcon />
                        </Link>

                        <button
                          type="button"
                          className={styles.actionBtn}
                          data-tooltip-id="delete-case"
                          data-tooltip-content={t('Delete')}
                          onClick={() => setDeleteConfirmModal({ isActive: true, id: narrative.id })}
                        >
                          <DeleteIcon />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
              <Tooltip id="move-case" place="bottom" style={{zIndex: '300'}}/>
              <Tooltip id="edit-case" place="bottom" style={{zIndex: '300'}}/>
              <Tooltip id="delete-case" place="bottom" style={{zIndex: '300'}}/>
            </tbody>
          </table>
      </div> : <div className='noData'>{t('There is no data to display')}</div>}

      {narratives && (
        <Paginator
          size={displayedNarratives.size}
          page={displayedNarratives.page}
          total={displayedNarratives.total}
          onPageChange={(page) => {
            const params = { page };
            if (activeProjectId) params.project = activeProjectId;
            if (searchQuery) params.q = searchQuery;
            setSearchParams(params);
          }}
        />
      )}

      <CreateProjectModal
        isVisible={createProjectModalOpen}
        onClose={() => setCreateProjectModalOpen(false)}
        onSuccess={handleProjectCreated}
      />

      <MoveToProjectModal
        isVisible={moveToProjectModal.isOpen}
        onClose={() => setMoveToProjectModal({ isOpen: false, caseId: null, sourceProjectId: null })}
        onSuccess={handleMoveSuccess}
        caseId={moveToProjectModal.caseId}
        sourceProjectId={moveToProjectModal.sourceProjectId}
        projects={projects}
      />

      <ManageProjectsModal
        isVisible={manageProjectsModalOpen}
        onClose={handleCloseManageProjects}
        projects={projects}
        onProjectsChange={setProjects}
        onCreateProject={() => {
          handleCloseManageProjects();
          setCreateProjectModalOpen(true);
        }}
      />

      <Modal
        isVisible={deleteConfirmModal.isActive}
        title={t('Delete')}
        content={<p>{t('Are you sure you want to delete a narrative?')}</p>}
        footer={
          <>
            <button
              className="btn-danger"
              onClick={() => handleDelete(deleteConfirmModal.id)}
              disabled={isDeleting}
            >
              {t('Delete')}
            </button>
            <button
              className="btn-reset"
              onClick={() =>
                setDeleteConfirmModal({ isActive: false, id: null })
              }
            >
              {t('Cancel')}
            </button>
          </>
        }
        onClose={() => setDeleteConfirmModal({ isActive: false, id: null })}
      />
    </div>
  );
}
